/*!
 * EDD Metaobject Renderer + Checkout Sync
 * Horizon version
 * - Supports old Dawn markup: data-edd="message"
 * - Supports newer LRC markup: data-lrc-edd-box / data-lrc-edd-text
 * - Syncs plain-text EDD into one visible line item property: Timeline
 */

/* ================================
   BLOCK 1: Render EDD on page/cart
==================================*/
(function () {
  function parseJSONSafe(s, fallback) {
    try {
      return JSON.parse(s);
    } catch (e) {
      return fallback;
    }
  }

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function dateKey(date) {
    return (
      date.getFullYear() +
      '-' +
      pad(date.getMonth() + 1) +
      '-' +
      pad(date.getDate())
    );
  }

  function toDateOnly(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  function isHoliday(d, holidays) {
    var ds = dateKey(d);
    return Array.isArray(holidays) && holidays.indexOf(ds) >= 0;
  }

  function addDays(date, days) {
    var d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  function addBusinessDays(start, days, holidays) {
    var d = new Date(start);
    var added = 0;

    while (added < days) {
      d = addDays(d, 1);

      var day = d.getDay();
      var isWeekend = day === 0 || day === 6;

      if (isWeekend) continue;
      if (isHoliday(d, holidays)) continue;

      added++;
    }

    return d;
  }

  function formatRange(start, end) {
    var opts = {
      month: 'short',
      day: 'numeric'
    };

    var s = start.toLocaleDateString(undefined, opts);
    var e = end.toLocaleDateString(undefined, opts);

    if (dateKey(start) === dateKey(end)) {
      return s;
    }

    return s + ' – ' + e;
  }

  function normalizeHolidays(raw) {
    if (!raw) return [];

    if (Array.isArray(raw)) {
      return raw;
    }

    if (typeof raw !== 'string') {
      return [];
    }

    var parsed = parseJSONSafe(raw, null);

    if (Array.isArray(parsed)) {
      return parsed.map(function (item) {
        if (typeof item === 'string') return item.slice(0, 10);
        if (item && item.date) return String(item.date).slice(0, 10);
        return '';
      }).filter(Boolean);
    }

    if (parsed && typeof parsed === 'object') {
      return Object.keys(parsed).map(function (key) {
        var item = parsed[key];

        if (typeof item === 'string') return item.slice(0, 10);
        if (item && item.date) return String(item.date).slice(0, 10);

        return '';
      }).filter(Boolean);
    }

    var matches = raw.match(/\d{4}-\d{2}-\d{2}/g);

    return matches || [];
  }

  function computeWindow(cfg) {
    var now = new Date();
    var holidays = normalizeHolidays(cfg.holidays);

    var startBase = toDateOnly(now);
    var cutoff = Math.max(0, Math.min(23, cfg.cutoffHour || 0));

    if (cutoff > 0 && now.getHours() >= cutoff) {
      startBase = addDays(startBase, 1);

      if (cfg.useBusinessDays) {
        while (
          startBase.getDay() === 0 ||
          startBase.getDay() === 6 ||
          isHoliday(startBase, holidays)
        ) {
          startBase = addDays(startBase, 1);
        }
      }
    }

    var start;
    var end;

    if (cfg.useBusinessDays) {
      start = cfg.minDays > 0 ? addBusinessDays(startBase, cfg.minDays, holidays) : startBase;
      end = cfg.maxDays > 0 ? addBusinessDays(startBase, cfg.maxDays, holidays) : start;
    } else {
      start = cfg.minDays > 0 ? addDays(startBase, cfg.minDays) : startBase;
      end = cfg.maxDays > 0 ? addDays(startBase, cfg.maxDays) : start;
    }

    return {
      start: start,
      end: end
    };
  }

  function renderOldEDDOne(el) {
    var min = parseInt(el.getAttribute('data-edd-min') || '0', 10);
    var max = parseInt(el.getAttribute('data-edd-max') || '0', 10);

    if (isNaN(min) || isNaN(max)) {
      el.style.display = 'none';
      return;
    }

    var useBiz = String(el.getAttribute('data-edd-business')).toLowerCase() === 'true';
    var cutoff = parseInt(el.getAttribute('data-edd-cutoff') || '0', 10);
    var holidays = el.getAttribute('data-edd-holidays') || '[]';
    var extra = el.getAttribute('data-edd-extra') || '';
    var label = el.getAttribute('data-edd-label') || '';

    var win = computeWindow({
      minDays: min,
      maxDays: max,
      useBusinessDays: useBiz,
      cutoffHour: cutoff,
      holidays: holidays
    });

    var range = formatRange(win.start, win.end);
    var prefix = label && label.trim().length ? label.trim() : 'Ships between';

    var html = prefix + ' ' + range;

    if (extra && extra.trim().length) {
      html += ' ' + extra;
    }

    var span = el.querySelector('[data-edd="text"]');

    if (!span) {
      span = document.createElement('span');
      span.setAttribute('data-edd', 'text');
      el.appendChild(span);
    }

    span.innerHTML = html;
    el.style.display = '';
  }

  function renderLRCEDDOne(el) {
    var min = parseInt(el.getAttribute('data-lrc-edd-min') || '0', 10);
    var max = parseInt(el.getAttribute('data-lrc-edd-max') || '0', 10);

    if (isNaN(min) || isNaN(max)) {
      el.hidden = true;
      return;
    }

    var useBiz = String(el.getAttribute('data-lrc-edd-business')).toLowerCase() === 'true';
    var cutoff = parseInt(el.getAttribute('data-lrc-edd-cutoff') || '0', 10);
    var holidays = el.getAttribute('data-lrc-edd-holidays') || '';
    var extra = el.getAttribute('data-lrc-edd-extra') || '';
    var label = el.getAttribute('data-lrc-edd-label') || '';

    var win = computeWindow({
      minDays: min,
      maxDays: max,
      useBusinessDays: useBiz,
      cutoffHour: cutoff,
      holidays: holidays
    });

    var range = formatRange(win.start, win.end);
    var prefix = label && label.trim().length ? label.trim() : 'Ships between';

    var text = prefix + ' ' + range;

    if (extra && extra.trim().length) {
      text += '. ' + extra.trim().replace(/^[.\s]+/, '');
    } else {
      text += '.';
    }

    var span = el.querySelector('[data-lrc-edd-text]');

    if (!span) {
      span = document.createElement('span');
      span.setAttribute('data-lrc-edd-text', '');
      el.appendChild(span);
    }

    span.textContent = text;
    el.hidden = false;
  }

  function renderAll(scope) {
    scope = scope || document;

    scope.querySelectorAll('[data-edd="message"]').forEach(renderOldEDDOne);
    scope.querySelectorAll('[data-lrc-edd-cart-box]').forEach(renderLRCEDDOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      renderAll(document);
    });
  } else {
    renderAll(document);
  }

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) renderAll(document);
  });

  document.addEventListener('cart:updated', function () {
    renderAll(document);
  });

  document.addEventListener('product:variant-change', function () {
    renderAll(document);
  });

  window.EDDMetaobject = {
    refresh: function (scope) {
      renderAll(scope || document);
    }
  };
})();

/* ================================================
   BLOCK 2: Sync plain-text EDD into line properties
   Horizon version
   Keeps ONLY "Timeline" visible in checkout/admin
===================================================*/
(function () {
  function debounce(fn, wait) {
    var t;

    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  var syncing = false;
  var debouncedSync = debounce(syncCartEDD, 300);

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function getCartRows() {
    var horizonRows = document.querySelectorAll('tr.cart-items__table-row[data-key]');

    if (horizonRows.length) {
      return Array.prototype.slice.call(horizonRows);
    }

    return Array.prototype.slice.call(document.querySelectorAll('tr.cart-item'));
  }

  function getTimelineTextFromRow(row) {
    var lrcEddEl = row.querySelector('[data-lrc-edd-cart-box] [data-lrc-edd-text]');
    var oldEddEl = row.querySelector('[data-edd="message"] [data-edd="text"]');

    if (lrcEddEl && lrcEddEl.textContent.trim()) {
      return lrcEddEl.textContent.trim();
    }

    if (oldEddEl && oldEddEl.textContent.trim()) {
      return oldEddEl.textContent.trim();
    }

    return '';
  }

  function collectCartLineEDDs() {
    var rows = getCartRows();
    var out = [];
    var seen = {};

    rows.forEach(function (row) {
      var key = row.getAttribute('data-key');
      var line = null;

      if (!key) {
        var qtyInput = row.querySelector('.quantity__input[data-index]');
        line = qtyInput ? parseInt(qtyInput.getAttribute('data-index'), 10) : null;
      }

      var eddText = getTimelineTextFromRow(row);

      if (!eddText) return;

      var dedupeKey = key || String(line || '');

      if (!dedupeKey || seen[dedupeKey]) return;

      seen[dedupeKey] = true;

      out.push({
        key: key,
        line: line,
        text: eddText,
        row: row
      });
    });

    return out;
  }

  async function getCartJSON() {
    var res = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json'
      }
    });

    if (!res.ok) {
      throw new Error('cart.js fetch failed');
    }

    return res.json();
  }

  function copyProperties(existingProperties) {
    var props = {};

    if (!existingProperties) {
      return props;
    }

    for (var k in existingProperties) {
      if (Object.prototype.hasOwnProperty.call(existingProperties, k)) {
        props[k] = existingProperties[k];
      }
    }

    return props;
  }

  function cleanEDDProperties(props) {
    if (props.EDD) delete props.EDD;
    if (props._EDD) delete props._EDD;
    if (props['Lead time']) delete props['Lead time'];

    return props;
  }

  async function syncCartEDD() {
    if (syncing) return;

    var rows = getCartRows();

    if (!rows.length) return;

    syncing = true;

    try {
      if (window.EDDMetaobject && typeof window.EDDMetaobject.refresh === 'function') {
        window.EDDMetaobject.refresh(document);
      }

      var cart = await getCartJSON();

      var byKey = {};
      var byLine = {};

      cart.items.forEach(function (item, i) {
        byKey[item.key] = item;
        byLine[i + 1] = item;
      });

      var lines = collectCartLineEDDs();

      for (var i = 0; i < lines.length; i++) {
        var l = lines[i];
        var item = l.key ? byKey[l.key] : byLine[l.line];

        if (!item) continue;

        var currentProps = item.properties || {};
        var currentTimeline = currentProps.Timeline || '';
        var lastSynced = l.row.getAttribute('data-edd-synced') || '';

        if (currentTimeline === l.text && lastSynced === l.text) {
          continue;
        }

        var props = copyProperties(currentProps);

        cleanEDDProperties(props);

        props.Timeline = l.text;

        var body = {
          quantity: item.quantity,
          properties: props
        };

        if (l.key) {
          body.id = l.key;
        } else if (l.line) {
          body.line = l.line;
        } else {
          continue;
        }

        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(body)
        }).catch(function () {});

        l.row.setAttribute('data-edd-synced', l.text);
      }
    } catch (e) {
      /*
        Silent fail is intentional.
        If sync fails, the cart still works.
      */
    } finally {
      syncing = false;
    }
  }

  ready(function () {
    setTimeout(syncCartEDD, 350);
    setTimeout(syncCartEDD, 1000);
    setTimeout(syncCartEDD, 2000);
  });

  document.addEventListener('change', debouncedSync);
  document.addEventListener('cart:updated', debouncedSync);
  document.addEventListener('product:added', debouncedSync);
  document.addEventListener('cart-drawer:open', debouncedSync);
  document.addEventListener('EDD:refresh', debouncedSync);

  document.addEventListener('click', function (e) {
    var target = e.target && e.target.closest ? e.target.closest('button, a') : null;

    if (!target) return;

    var text = (target.textContent || '').toLowerCase();
    var href = target.getAttribute ? target.getAttribute('href') || '' : '';

    if (
      text.indexOf('checkout') !== -1 ||
      target.name === 'checkout' ||
      href.indexOf('/checkout') !== -1
    ) {
      syncCartEDD();
    }
  });

  ready(function () {
    if (!window.MutationObserver || !document.body) return;

    var observer = new MutationObserver(function () {
      debouncedSync();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  });

  window.LRCEDDCheckoutSync = {
    sync: syncCartEDD
  };
})();